import ApiSmile from '@/api/api-smile';
import PageData from '@/types/page-data';
import LoadingUtil from '@/util/loading-util';
import MessageUtil from '@/util/message-util';
import React, {useEffect, useState} from 'react';
import './hold-list.scss';
import PageParam from '@/types/page-param';
import LabelUtil from '@/util/label-util';
import PageContainer from '@/component/page-container';
import PaginationTable, {ColumnType} from '@/component/pagination-table';
import Utils from '@/util/utils';
import {HoldState} from '@/model/const';
import {ProfileOutlined} from '@ant-design/icons';
import {NavigateFunction, useNavigate} from 'react-router';
import {HoldData} from '../types';
import ActionArea from '@/component/action-area';
import {ActionButton} from '@/component/action-area/action-area';
import HoldUtil from '@/util/hold-util';
import CheckBoxFilter from '@/component/checkbox-filter';
import {Button, CheckboxOptionType} from 'antd';

const holdFilterOption: CheckboxOptionType[] = [
    {
        label: '持有',
        value: HoldState.BOUGHT,
    },
    {
        label: '清仓',
        value: HoldState.SOLD,
    },
];

const getQueryFilter = (hold?: string[]) => {
    const getFilter = (array?: string[]) => {
        if (!array) return undefined;
        if (array.length === 1 && array[0] === 'ALL') {
            return undefined;
        }
        return array;
    };

    return {
        hold: getFilter(hold),
    };
};

const getColumnData = (navigate: NavigateFunction): ColumnType<HoldData>[] => {
    const onPressDetail = (record: HoldData) => {
        navigate(`/hold/${record.id}`);
    };

    return [
        {
            title: '代码',
            dataIndex: 'code',
            key: 'code',
        },
        {
            title: '名称',
            dataIndex: 'name',
            key: 'name',
        },
        {
            title: '持有状态',
            dataIndex: 'holdState',
            key: 'holdState',
            render: (text: string) => LabelUtil.getHoldState(text),
        },
        {
            title: '数量',
            dataIndex: 'quantity',
            key: 'quantity',
        },
        {
            title: '买入时间',
            dataIndex: 'buyTime',
            key: 'buyTime',
            render: (text: number) => Utils.formatDate(text),
        },
        {
            title: '买入价格',
            dataIndex: 'buyPrice',
            key: 'buyPrice',
            render: (text: number) => Utils.formatPrice(text),
        },
        {
            title: '卖出时间',
            dataIndex: 'sellTime',
            key: 'sellTime',
            render: (text: number, record: HoldData) => (record.holdState === HoldState.SOLD ? Utils.formatDate(text) : '--'),
        },
        {
            title: '卖出价格',
            dataIndex: 'sellPrice',
            key: 'sellPrice',
            render: (text: number, record: HoldData) => (record.holdState === HoldState.SOLD ? Utils.formatPrice(text) : '--'),
        },
        {
            title: '收益',
            dataIndex: '',
            key: 'profit',
            render: (_text: number, record: HoldData) => HoldUtil.getProfit(record),
        },
        {
            title: '收益率',
            dataIndex: '',
            key: 'profitRate',
            render: (_text: number, record: HoldData) => HoldUtil.getProfitRate(record),
        },
        {
            title: '详情',
            dataIndex: 'detail',
            key: 'detail',
            render: (_text: string, record: HoldData) => (
                <div className="detail-icon" onClick={() => onPressDetail(record)}>
                    <ProfileOutlined />
                </div>
            ),
        },
    ];
};

interface QueryFilter {
    hold?: string[];
}

const HoldList = () => {
    const [data, setData] = useState<PageData<HoldData>>();
    const [pageParam, setPageParam] = useState<PageParam>({page: 0, size: 10});
    const [holdState, setHoldState] = useState<string[]>(['ALL']);
    const [queryFilter, setQueryFilter] = useState<QueryFilter>(getQueryFilter(holdState));
    const navigate = useNavigate();

    useEffect(() => {
        const load = async () => {
            LoadingUtil.show();
            const response: any = await ApiSmile.getHoldList(pageParam, queryFilter);
            if (response?.data?.code === 0) {
                setData(response.data.data);
            } else {
                const msg = response?.data?.msg || '网络请求失败';
                MessageUtil.error(msg);
            }
            LoadingUtil.hide();
        };

        load();
    }, [pageParam, queryFilter]);

    const onBuyClick = () => {
        navigate('/hold/buy');
    };

    const onSellClick = () => {
        navigate('/hold/sell');
    };

    const getButtons = () => {
        const buttons: ActionButton[] = [
            {
                text: '买入',
                onClick: onBuyClick,
            },
            {
                text: '卖出',
                onClick: onSellClick,
            },
        ];
        return buttons;
    };

    const handleUpdateClick = () => {
        setPageParam({...pageParam, page: 0});
        setQueryFilter(getQueryFilter(holdState));
    };

    const renderFilter = () => {
        return (
            <div className="filter-container">
                <CheckBoxFilter
                    title="持有状态："
                    single
                    options={holdFilterOption}
                    value={holdState}
                    onChange={(checked) => setHoldState(checked as string[])}
                />
                <Button className="button button-first" onClick={handleUpdateClick}>
                    刷新
                </Button>
            </div>
        );
    };

    return (
        <PageContainer className="hold-list">
            <ActionArea buttons={getButtons()} />
            {renderFilter()}
            <PaginationTable columns={getColumnData(navigate)} data={data} setPageParam={setPageParam} />
        </PageContainer>
    );
};

export default HoldList;
